import React from 'react';
import * as Headless from '@headlessui/react'
import clsx from 'clsx'

import { Text } from '@/components/reusable/typography'

const sizes = {
  xs: 'sm:max-w-xs',
  sm: 'sm:max-w-sm',
  md: 'sm:max-w-md',
  lg: 'sm:max-w-lg',
  xl: 'sm:max-w-xl',
  '2xl': 'sm:max-w-2xl',
  '3xl': 'sm:max-w-3xl',
  '4xl': 'sm:max-w-4xl',
  '5xl': 'sm:max-w-5xl',
}

/**
 * Alert — V2 Dialog base
 *
 * @param {string} size - Panel width: xs|sm|md|lg|xl|2xl|3xl|4xl|5xl
 * @param {number} zIndex - z-index for backdrop and panel (default 10)
 * @param {boolean} dismissible - Show/hide the close button (default true).
 *   Set to false during processing states to prevent user dismissal.
 */
export function Alert({ size = 'md', className, children, zIndex = 10, dismissible = true, ...props }) {
  return (
    <Headless.Dialog {...props}>
      <Headless.DialogBackdrop
        transition
        className="fixed inset-0 flex w-screen justify-center overflow-y-auto bg-black/30 backdrop-blur-xs transition duration-100 focus:outline-0 data-closed:opacity-0 data-enter:ease-out data-leave:ease-in sm:px-6 sm:py-8 lg:px-8 lg:py-16"
        style={{ zIndex }}
      />
      <div className="fixed inset-0 w-screen overflow-y-auto pt-6 sm:pt-0" style={{ zIndex }}>
        <div className="grid min-h-full grid-rows-[1fr_auto] justify-items-center sm:grid-rows-[1fr_auto_3fr] sm:p-10">
          <Headless.DialogPanel
            transition
            className={clsx(
              className,
              sizes[size],
              'row-start-2 w-full min-w-0 rounded-t-2xl bg-white dark:bg-zinc-900 p-(--gutter) shadow-sm [--gutter:--spacing(10)] sm:mb-auto sm:rounded-2xl forced-colors:outline-solid',
              'transition duration-100 will-change-transform data-closed:translate-y-12 data-closed:opacity-0 data-enter:ease-out data-leave:ease-in sm:data-closed:translate-y-0 sm:data-closed:data-enter:scale-95'
            )}
          >
            {/* Close button — hidden during processing */}
            {dismissible && (
              <Headless.CloseButton
                aria-label="Close"
                className="absolute top-5 right-5 p-1.5 rounded-full bg-zinc-100 text-black transition-colors hover:bg-zinc-200 focus:outline-hidden cursor-pointer"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="size-4">
                  <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
                </svg>
              </Headless.CloseButton>
            )}
            {children}
          </Headless.DialogPanel>
        </div>
      </div>
    </Headless.Dialog>
  )
}

/**
 * AlertTitle — Standard dialog title
 * Renders as h4 with the v2 dialog title pattern.
 * Change once here → every dialog updates.
 */
export function AlertTitle({ className, children, ...props }) {
  return (
    <Headless.DialogTitle
      as="h4"
      {...props}
      className={clsx('mb-4 sm:pr-12', className)}
    >
      {children}
    </Headless.DialogTitle>
  )
}

/**
 * AlertDescription — Standard dialog description text
 * Uses v2 Text with the standard text-base/5 pattern.
 */
export function AlertDescription({ className, children, ...props }) {
  return (
    <Text {...props} className={clsx('sm:pr-12', className)}>
      {children}
    </Text>
  )
}

/**
 * AlertBody — Content area below the title/description
 */
export function AlertBody({ className, ...props }) {
  return <div {...props} className={clsx(className, 'mt-4')} />
}

/**
 * AlertActions — Standard dialog button row
 * Right-aligned with consistent gap and top margin.
 */
export function AlertActions({ className, ...props }) {
  return (
    <div
      {...props}
      className={clsx(
        'flex justify-end gap-4 mt-8',
        className
      )}
    />
  )
}
